// alarmStateMachine.js
// Máquina de estados da notificação de alarmes críticos.
//
// Lógica pura, sem React, sem DOM e sem relógio próprio: o instante entra como
// argumento (`now`) e a persistência é responsabilidade de quem chama. É aqui
// que mora a regra de negócio da feature, e é por isso que este é o módulo
// coberto por testes unitários.
//
// Escala por ativo (campo `alarmed` na raiz da mensagem):
//   0 normal · 1 moderado · 2 crítico
// O estado de conexão (`offline`) trafega em campo separado e NÃO integra a
// escala — perder sinal não é o mesmo que normalizar.

export const NORMAL = 0;
export const MODERATE = 1;
export const CRITICAL = 2;

// O ativo precisa permanecer em crítico por esta janela antes de notificar,
// para que oscilação curta não dispare alarme sonoro por condição já resolvida.
export const CONFIRMATION_WINDOW_MS = 3000;

/**
 * Cria o estado inicial.
 *
 * @param snapshot  Retrato dos níveis já conhecidos ({ [ToSenseID]: nível }).
 *                  Vindo da sessão, é ele que impede que um ativo já crítico
 *                  na abertura — ou após um reload — gere aviso novo.
 * @param context   Lista de ToSenseIDs sob responsabilidade do operador.
 *                  `null` significa "sem restrição de contexto".
 */
export const createState = ({ snapshot = {}, context = null } = {}) => ({
  snapshot: { ...snapshot },
  context: context ? [...context] : null,
  // Ativos que entraram em crítico e ainda cumprem a janela de confirmação.
  candidates: {},
  // Avisos confirmados, na ordem em que foram disparados.
  notices: [],
});

const inContext = (state, assetId) =>
  state.context === null || state.context.includes(assetId);

const withoutAsset = (map, assetId) => {
  if (!(assetId in map)) return map;
  const next = { ...map };
  delete next[assetId];
  return next;
};

/**
 * Processa um frame do canal de tempo real.
 *
 * Um frame com `offline === true` é ignorado por completo: sem sinal não há
 * como afirmar que o ativo normalizou, então nada avança e nada se encerra —
 * o aviso pendente permanece exatamente como estava.
 */
export const applyFrame = (state, frame, now) => {
  const assetId = frame?.ToSenseID;
  if (!assetId) return state;
  if (!inContext(state, assetId)) return state;
  if (frame.offline === true) return state;

  const level = frame.alarmed;
  if (level !== NORMAL && level !== MODERATE && level !== CRITICAL) {
    return state;
  }

  const previous = state.snapshot[assetId];
  const snapshot = { ...state.snapshot, [assetId]: level };

  // Primeiro contato com o ativo nesta sessão: só registra o retrato. É o que
  // garante que um ativo já crítico na abertura não gere aviso.
  if (previous === undefined) {
    return { ...state, snapshot };
  }

  if (level === CRITICAL) {
    // Só a transição para crítico abre janela. Permanecer em crítico — inclusive
    // depois de o operador fechar o aviso — não reabre nada.
    //
    // Basta comparar com o retrato: candidato e aviso só passam a existir junto
    // com o retrato em CRITICAL, e ambos são removidos quando o ativo cai
    // abaixo disso. Logo `previous === CRITICAL` já cobre os três casos.
    //
    // Devolver o MESMO estado importa: o canal transmite todos os ativos em
    // ciclo, e um objeto novo por frame dispararia re-render da aplicação
    // inteira e uma escrita síncrona no sessionStorage.
    if (previous === CRITICAL) return state;
    return {
      ...state,
      snapshot,
      candidates: { ...state.candidates, [assetId]: now },
    };
  }

  // Abaixo de crítico não há candidato nem aviso a manter: ambos só existem
  // com o retrato em CRITICAL, e `previous === level` aqui já implica que o
  // ativo não estava crítico. Nada mudou, então devolve o MESMO estado.
  if (previous === level) return state;

  // Saída do estado crítico: cancela a janela em curso e encerra o aviso
  // automaticamente, sem exigir ação do operador.
  return {
    ...state,
    snapshot,
    candidates: withoutAsset(state.candidates, assetId),
    notices: state.notices.filter((n) => n.assetId !== assetId),
  };
};

/**
 * Promove a aviso todo candidato que já cumpriu a janela de confirmação.
 * Deve ser chamada periodicamente pela camada que detém o relógio.
 */
export const tick = (state, now) => {
  const matured = Object.entries(state.candidates).filter(
    ([, since]) => now - since >= CONFIRMATION_WINDOW_MS,
  );
  if (matured.length === 0) return state;

  const candidates = { ...state.candidates };
  const created = matured.map(([assetId, since]) => {
    delete candidates[assetId];
    return { assetId, level: CRITICAL, since, confirmedAt: now };
  });

  return { ...state, candidates, notices: [...state.notices, ...created] };
};

/** Encerra o aviso por ação do operador (fechar ou reconhecer). */
export const dismiss = (state, assetId) => {
  if (!state.notices.some((n) => n.assetId === assetId)) return state;
  return {
    ...state,
    notices: state.notices.filter((n) => n.assetId !== assetId),
  };
};

/**
 * Reconhecer encerra o aviso da mesma forma que fechar. A distinção existe
 * para quem chama: reconhecer também aciona o fluxo de reconhecimento no
 * back-end, que é responsabilidade da camada de interface.
 */
export const acknowledge = dismiss;

/**
 * Registra o nível corrente de ativos ainda desconhecidos, sem notificar.
 *
 * É o "registrar o estado de alarme corrente de cada ativo no início da sessão"
 * do escopo. Sem isso o retrato se forma preguiçosamente, frame a frame, e um
 * ativo cujo primeiro frame se perdeu aparece depois já crítico — indistinguível
 * de quem já estava crítico na abertura, e portanto nunca notificado.
 *
 * Só preenche lacunas: nível já observado nesta sessão nunca é sobrescrito, para
 * que a carga inicial não apague uma transição em andamento.
 */
export const seedSnapshot = (state, levels) => {
  const missing = Object.entries(levels || {}).filter(
    ([assetId, level]) =>
      state.snapshot[assetId] === undefined &&
      (level === NORMAL || level === MODERATE || level === CRITICAL) &&
      inContext(state, assetId),
  );
  if (missing.length === 0) return state;
  return {
    ...state,
    snapshot: { ...state.snapshot, ...Object.fromEntries(missing) },
  };
};

/**
 * Reabre aviso, na abertura da sessão, para ativos que seguem críticos com
 * alarme ABERTO e NÃO RECONHECIDO por ninguém.
 *
 * Complementa `seedSnapshot`, que sozinho deixava um crítico não tratado
 * invisível após um F5 ou uma troca de turno: o retrato registrava o nível e,
 * como não havia transição, nenhum aviso nascia. O operador que assumia o turno
 * não tinha como saber que o poço estava em alarme.
 *
 * A memória aqui é o reconhecimento no servidor, não a sessão do navegador —
 * por isso o que já foi tratado por alguém não volta a incomodar, e o que
 * ninguém tratou reaparece a cada abertura até ser reconhecido.
 *
 * Não há janela de confirmação: a permanência em crítico já está estabelecida
 * pelo alarme aberto no servidor, que é prova mais forte que 3 segundos de
 * frames. O aviso nasce marcado com `restored` para que a interface possa
 * distingui-lo de uma transição recém-observada.
 */
export const seedNotices = (state, assetIds, now) => {
  // A deduplicação é obrigatória, não defensiva: um ativo com vários alarmes
  // abertos aparece repetido na lista, e o filtro compara contra o estado
  // ORIGINAL — sem isto, cada repetição criaria um aviso do mesmo ativo.
  const novos = [...new Set(assetIds || [])].filter(
    (assetId) =>
      inContext(state, assetId) &&
      state.snapshot[assetId] === CRITICAL &&
      !state.notices.some((n) => n.assetId === assetId),
  );
  if (novos.length === 0) return state;

  return {
    ...state,
    // Um ativo restaurado não pode continuar como candidato: o aviso dele já
    // existe, e a maturação da janela criaria um segundo aviso do mesmo ativo.
    candidates: novos.reduce(
      (acc, assetId) => withoutAsset(acc, assetId),
      state.candidates,
    ),
    notices: [
      ...state.notices,
      ...novos.map((assetId) => ({
        assetId,
        level: CRITICAL,
        since: now,
        confirmedAt: now,
        restored: true,
      })),
    ],
  };
};

/** Substitui o contexto do operador, descartando o que saiu do escopo. */
export const setContext = (state, context) => {
  const next = context ? [...context] : null;
  const scoped = { ...state, context: next };
  if (next === null) return scoped;
  return {
    ...scoped,
    candidates: Object.fromEntries(
      Object.entries(state.candidates).filter(([assetId]) =>
        next.includes(assetId),
      ),
    ),
    notices: state.notices.filter((n) => next.includes(n.assetId)),
  };
};
